import type { DataViewSelection } from '@blocksuite/data-view';
import {
  CalendarViewSelectionWithTypeSchema,
  GanttViewSelectionWithTypeSchema,
  KanbanViewSelectionWithTypeSchema,
  TableViewSelectionWithTypeSchema,
} from '@blocksuite/data-view/view-presets';
import { BaseSelection, SelectionExtension } from '@blocksuite/store';
import { z } from 'zod';

const ViewSelectionSchema = z.union([
  TableViewSelectionWithTypeSchema,
  KanbanViewSelectionWithTypeSchema,
  GanttViewSelectionWithTypeSchema,
  CalendarViewSelectionWithTypeSchema,
]);

/**
 * Selections live in the store, so every editor host over the same store sees
 * them. A database rendered twice at once (canvas live table and fullscreen
 * editor) must only follow the selection made on its own surface.
 */
export const DATABASE_SELECTION_SURFACES = [
  'page',
  'canvas',
  'fullscreen',
] as const;

export type DatabaseSelectionSurface =
  (typeof DATABASE_SELECTION_SURFACES)[number];

const DatabaseSelectionSchema = z.object({
  blockId: z.string(),
  surface: z.enum(DATABASE_SELECTION_SURFACES),
  viewSelection: ViewSelectionSchema,
});

export class DatabaseSelection extends BaseSelection {
  static override group = 'note';

  static override type = 'database';

  readonly surface: DatabaseSelectionSurface;

  readonly viewSelection: DataViewSelection;

  get viewId() {
    return this.viewSelection.viewId;
  }

  constructor({
    blockId,
    surface,
    viewSelection,
  }: {
    blockId: string;
    surface: DatabaseSelectionSurface;
    viewSelection: DataViewSelection;
  }) {
    super({
      blockId,
    });

    this.surface = surface;
    this.viewSelection = viewSelection;
  }

  static override fromJSON(json: Record<string, unknown>): DatabaseSelection {
    const { blockId, surface, viewSelection } =
      DatabaseSelectionSchema.parse(json);
    return new DatabaseSelection({
      blockId,
      surface,
      viewSelection: viewSelection,
    });
  }

  isOn(blockId: string, surface: DatabaseSelectionSurface): boolean {
    return this.blockId === blockId && this.surface === surface;
  }

  override equals(other: BaseSelection): boolean {
    if (!(other instanceof DatabaseSelection)) {
      return false;
    }
    return this.blockId === other.blockId && this.surface === other.surface;
  }

  override toJSON(): Record<string, unknown> {
    return {
      type: 'database',
      blockId: this.blockId,
      surface: this.surface,
      viewSelection: this.viewSelection,
    };
  }
}

export const DatabaseSelectionExtension = SelectionExtension(DatabaseSelection);
